/**
 * Minimal ProfileBlob interface for the profile-status module.
 *
 * Mirrors the iOS `ProfileBlob` type used by
 * `ProfileStatusValidation`, `ProfileStatusExpiry`, and
 * `ProfileStatusBuilder`. The full cryptographic blob (AES-GCM sealed,
 * HKDF-derived key) is handled by the separate ProfileBlobCipher layer;
 * this interface represents the plaintext payload.
 *
 * `statusExpiresAt` is a unix-seconds timestamp as a 64-bit integer.
 * In JavaScript we use `number` (safe for unix-seconds values well
 * beyond year 2100). `null` means "no expiry".
 */
export interface ProfileBlob {
  /** Display name. Required. */
  readonly name: string;
  /** SHA-256 hash of the avatar image bytes, as a hex string. Optional. */
  readonly avatarHash?: string;
  /** Free-text about (up to 130 unicode scalars). Optional. */
  readonly about?: string;
  /** Single-scalar emoji status. Optional. */
  readonly emojiStatus?: string;
  /** Unix-seconds expiry for emojiStatus. null = no expiry. Optional. */
  readonly statusExpiresAt?: number | null;
  /** Schema version for forward-compat. Mirrors iOS ProfileBlob.currentSchema. */
  readonly schema?: number;
}

/** Current schema version, mirroring iOS ProfileBlob.currentSchema. */
export const PROFILE_BLOB_CURRENT_SCHEMA = 1;
